import { NumberField } from "@/components/ui/number-field";
import { useState, useEffect, useRef, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation, Link } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type { MeditationPlaylist, MeditationSong, CustomMeditation, MediaFile, DriveFile } from "@shared/schema";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useVoiceRecorder } from "@/hooks/useVoiceRecorder";
import { useTextToSpeech } from "@/hooks/useTextToSpeech";
import { useAudioMixer } from "@/hooks/useAudioMixer";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Plus, Music, Trash2, Edit, Mic, Square, ArrowLeft, Play, Pause, Volume2, Trash, PlayCircle, Link2, FolderOpen, Youtube, Cloud, ExternalLink } from "lucide-react";
import { Switch } from "@/components/ui/switch";

// Hand-written to avoid drizzle-zod + Zod 3.25 incompatibility (.omit().extend() throws at module init)
const songSchema = z.object({
  title: z.string().min(1, "Title is required"),
  url: z.string().min(1, "Audio source is required").refine(
    (value) => value.startsWith("http://") || value.startsWith("https://") || value.startsWith("data:") || value.startsWith("/api/"),
    { message: "Must be a valid URL or audio source" }
  ),
  artist: z.string().optional(),
  duration: z.number().optional(),
  sortOrder: z.number().optional(),
  librarySortOrder: z.number().optional(),
  subject: z.string().optional(),
  isStarred: z.boolean().optional(),
  tags: z.array(z.string()).optional(),
  mnmCategoryId: z.string().optional(),
  contentType: z.string().optional(),
});

// Hand-written to avoid drizzle-zod + Zod 3.25 incompatibility
const customMeditationSchema = z.object({
  name: z.string().min(1, "Name is required"),
  scriptText: z.string().min(10, "Script must be at least 10 characters"),
  voiceType: z.string().default("female"),
  voiceAudioUrl: z.string().optional(),
  backgroundMusicId: z.string().optional().nullable(),
  backgroundMusicUrl: z.string().optional().nullable(),
  musicVolume: z.number().optional(),
  voiceVolume: z.number().optional(),
  playbackSpeed: z.number().min(0).max(10).default(5),
  subject: z.string().optional(),
  isStarred: z.boolean().optional(),
  tags: z.array(z.string()).optional(),
  mnmCategoryId: z.string().optional(),
  contentType: z.string().optional(),
});

export default function MeditationSettingsPage() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const urlParams = new URLSearchParams(window.location.search);
  const initialTab = urlParams.get("tab") === "custom" ? "custom" : "playlist";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [showSongForm, setShowSongForm] = useState(false);
  const [showRecording, setShowRecording] = useState(false);
  const [isPreviewingFull, setIsPreviewingFull] = useState(false);
  const backgroundMusicAudioRef = useRef<HTMLAudioElement | null>(null);
  const hasCreatedDefaultPlaylist = useRef(false);
  const [songSourceType, setSongSourceType] = useState<"url" | "media" | "youtube" | "drive">("url");
  const [selectedMediaFile, setSelectedMediaFile] = useState<MediaFile | null>(null);
  const [selectedDriveFile, setSelectedDriveFile] = useState<DriveFile | null>(null);
  const [songContentType, setSongContentType] = useState<"meditation" | "media">("meditation");
  const [backgroundMusicEnabled, setBackgroundMusicEnabled] = useState(false);
  const [backgroundMusicSource, setBackgroundMusicSource] = useState<"library" | "url">("library");
  
  const { state: recorderState, controls: recorderControls } = useVoiceRecorder();
  const { state: ttsState, controls: ttsControls } = useTextToSpeech();
  const { state: mixerState, controls: mixerControls } = useAudioMixer();

  const { data: playlists = [], isLoading: playlistsLoading } = useQuery<MeditationPlaylist[]>({
    queryKey: ["/api/meditation/playlists"],
  });

  const userPlaylist = playlists[0];

  const { data: songs = [], isLoading: songsLoading } = useQuery<MeditationSong[]>({
    queryKey: ["/api/meditation/playlists", userPlaylist?.id, "songs"],
    enabled: !!userPlaylist?.id,
  });

  const { data: customMeditations = [], isLoading: customMeditationsLoading } = useQuery<CustomMeditation[]>({
    queryKey: ["/api/meditation/custom"],
  });

  // Fetch media files for audio selection (mp3, mpeg, wav, ogg, etc.)
  const { data: allMediaFiles = [] } = useQuery<MediaFile[]>({
    queryKey: ["/api/media"],
  });
  
  // Filter for audio files only
  const audioMediaFiles = allMediaFiles.filter(file => 
    ['mp3', 'mpeg', 'wav', 'ogg', 'audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/ogg', 'audio/x-wav'].includes(file.fileType.toLowerCase())
  );

  // Fetch Google Drive files
  const { data: driveFiles = [] } = useQuery<DriveFile[]>({
    queryKey: ["/api/google-drive/files"],
  });

  // Filter for audio files from Google Drive
  const audioDriveFiles = driveFiles.filter(file => 
    file.mimeType?.startsWith('audio/') || 
    file.name?.toLowerCase().endsWith('.mp3') ||
    file.name?.toLowerCase().endsWith('.wav') ||
    file.name?.toLowerCase().endsWith('.ogg') ||
    file.name?.toLowerCase().endsWith('.m4a')
  );

  const createPlaylistMutation = useMutation({
    mutationFn: async (data: any) => {
      return apiRequest("POST", "/api/meditation/playlists", data);
    },
    onSuccess: () => {
      console.log("Playlist created successfully");
      queryClient.invalidateQueries({ queryKey: ["/api/meditation/playlists"] });
    },
    onError: (error: any) => {
      console.error("Failed to create playlist:", error);
    },
  });

  const createSongMutation = useMutation({
    mutationFn: async (data: any) => {
      return apiRequest("POST", `/api/meditation/playlists/${userPlaylist?.id}/songs`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/meditation/playlists", userPlaylist?.id, "songs"] });
      queryClient.invalidateQueries({ queryKey: ["/api/meditation/library"] });
      setShowSongForm(false);
      setSongSourceType("url");
      setSongContentType("meditation");
      setSelectedMediaFile(null);
      setSelectedDriveFile(null);
      songForm.reset();
    },
  });

  const deleteSongMutation = useMutation({
    mutationFn: async (songId: string) => {
      return apiRequest("DELETE", `/api/meditation/songs/${songId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/meditation/playlists", userPlaylist?.id, "songs"] });
    },
  });

  const createCustomMeditationMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest("POST", "/api/meditation/custom", data);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to save meditation");
      }
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/meditation/custom"] });
      customMeditationForm.reset();
      toast({
        title: "Meditation Saved",
        description: "Your custom meditation has been saved successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error Saving Meditation",
        description: error.message || "There was a problem saving your meditation. Please try again.",
        variant: "destructive",
      });
    },
  });

  const deleteCustomMeditationMutation = useMutation({
    mutationFn: async (meditationId: string) => {
      return apiRequest("DELETE", `/api/meditation/custom/${meditationId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/meditation/custom"] });
    },
  });

  // Auto-create default playlist if none exists
  useEffect(() => {
    if (!playlistsLoading && playlists.length === 0 && !hasCreatedDefaultPlaylist.current) {
      hasCreatedDefaultPlaylist.current = true;
      createPlaylistMutation.mutate({ name: "My Meditation Playlist" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlistsLoading, playlists.length]);

  const songForm = useForm({
    resolver: zodResolver(songSchema),
    defaultValues: {
      title: "",
      artist: "",
      url: "",
      duration: 0,
      sortOrder: 0,
    },
  });

  const customMeditationForm = useForm({
    resolver: zodResolver(customMeditationSchema),
    defaultValues: {
      name: "",
      scriptText: "",
      voiceType: "nova",
      musicVolume: 30,
      voiceVolume: 70,
      backgroundMusicId: null,
      backgroundMusicUrl: null,
      playbackSpeed: 5,
    },
  });

  const voiceType = customMeditationForm.watch("voiceType");

  useEffect(() => {
    setShowRecording(voiceType === "recorded");
  }, [voiceType]);

  const handleAddSong = songForm.handleSubmit(async (data) => {
    if (!userPlaylist?.id) {
      return;
    }
    createSongMutation.mutate({ ...data, contentType: songContentType });
  });

  const handleSaveCustomMeditation = customMeditationForm.handleSubmit(async (data) => {
    if (data.voiceType === "recorded" && !recorderState.audioBase64) {
      toast({
        title: "Recording Required",
        description: "Please record your voice before saving the meditation.",
        variant: "destructive",
      });
      return;
    }

    const meditationData = {
      ...data,
      voiceAudioUrl: data.voiceType === "recorded" ? recorderState.audioBase64 : null,
      backgroundMusicId: backgroundMusicEnabled && backgroundMusicSource === "library" ? data.backgroundMusicId : null,
      backgroundMusicUrl: backgroundMusicEnabled && backgroundMusicSource === "url" ? data.backgroundMusicUrl : null,
      musicVolume: backgroundMusicEnabled ? data.musicVolume : 30,
    };
    
    createCustomMeditationMutation.mutate(meditationData);
  });

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return "N/A";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handlePreviewTTS = async () => {
    const scriptText = customMeditationForm.getValues("scriptText");
    const voiceType = customMeditationForm.getValues("voiceType");
    const voiceVolume = customMeditationForm.getValues("voiceVolume");

    if (!scriptText) {
      toast({
        title: "No Script",
        description: "Please enter a meditation script first",
        variant: "destructive",
      });
      return;
    }

    if (voiceType === "recorded") return;

    const validVoices = ["nova", "shimmer", "onyx", "echo", "alloy", "fable"];
    if (!voiceType || !validVoices.includes(voiceType)) return;

    setIsGeneratingTts(true);
    
    try {
      toast({
        title: "Generating Voice Preview",
        description: `Creating ${voiceType} AI voice...`,
      });
      
      const response = await fetch("/api/meditation/tts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          text: scriptText,
          voice: voiceType,
          format: "mp3"
        }),
      });
      
      if (!response.ok) {
        throw new Error("Failed to generate speech");
      }
      
      const result = await response.json();
      
      // Create audio element from base64
      const audioDataUrl = `data:${result.mimeType};base64,${result.audio}`;
      const audio = new Audio(audioDataUrl);
      audio.volume = (voiceVolume ?? 70) / 100;
      
      // Store reference for cleanup
      aiTtsAudioRef.current = audio;
      setIsPreviewingFull(true);
      
      audio.onended = () => {
        handleStopPreview();
      };
      
      await audio.play();
      
    } catch (error) {
      console.error("TTS preview error:", error);
      toast({
        title: "Voice Preview Failed",
        description: "Could not generate AI voice. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingTts(false);
    }
  };

  const handleStopPreview = useCallback(() => {
    setIsPreviewingFull(false);
    
    // Stop mixer (for recorded voice)
    mixerControls.stop();
    
    // Stop AI TTS audio if playing
    if (aiTtsAudioRef.current) {
      aiTtsAudioRef.current.pause();
      aiTtsAudioRef.current.currentTime = 0;
      aiTtsAudioRef.current = null;
    }
    
    // Stop background music audio
    if (backgroundMusicAudioRef.current) {
      backgroundMusicAudioRef.current.pause();
      backgroundMusicAudioRef.current.currentTime = 0;
      backgroundMusicAudioRef.current = null;
    }
  }, [mixerControls]);

  // Reference for AI TTS audio playback (declared after handleStopPreview since it's used via ref)
  const aiTtsAudioRef = useRef<HTMLAudioElement | null>(null);
  const [isGeneratingTts, setIsGeneratingTts] = useState(false);

  const handleHearHowItSounds = useCallback(async () => {
    const data = customMeditationForm.getValues();
    const backgroundMusicId = data.backgroundMusicId;
    const backgroundMusicUrl = data.backgroundMusicUrl;
    const voiceType = data.voiceType;
    const scriptText = data.scriptText;

    if (!scriptText || scriptText.trim() === "") {
      toast({
        title: "Script Required",
        description: "Please enter a meditation script before previewing.",
        variant: "destructive",
      });
      return;
    }

    // Stop any existing playback
    handleStopPreview();

    // Determine background music URL based on source
    let backgroundMusicPlayUrl: string | null = null;
    if (backgroundMusicEnabled) {
      if (backgroundMusicSource === "library" && backgroundMusicId) {
        const song = songs.find(s => s.id === backgroundMusicId);
        if (song) backgroundMusicPlayUrl = song.url;
      } else if (backgroundMusicSource === "url" && backgroundMusicUrl) {
        backgroundMusicPlayUrl = backgroundMusicUrl;
      }
    }

    setIsPreviewingFull(true);

    if (voiceType === "recorded") {
      // For recorded voice, use the mixer
      if (!recorderState.audioBlob) {
        setIsPreviewingFull(false);
        return;
      }

      // Set volumes
      mixerControls.setMusicVolume(data.musicVolume ?? 30);
      mixerControls.setVoiceVolume(data.voiceVolume ?? 70);

      await mixerControls.loadAndPlay(
        backgroundMusicPlayUrl ? { type: 'music', url: backgroundMusicPlayUrl } : undefined,
        { type: 'voice', blob: recorderState.audioBlob }
      );
    } else {
      // Use OpenAI AI voices (nova, shimmer, onyx, echo, alloy, fable)
      const validVoices = ["nova", "shimmer", "onyx", "echo", "alloy", "fable"];
      if (!voiceType || !validVoices.includes(voiceType)) {
        toast({
          title: "Invalid Voice",
          description: "Please select a valid voice option",
          variant: "destructive",
        });
        return;
      }
      
      setIsGeneratingTts(true);
      
      try {
        toast({
          title: "Generating Voice",
          description: `Creating ${voiceType} AI voice narration...`,
        });
        
        const response = await fetch("/api/meditation/tts", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            text: scriptText,
            voice: voiceType,
            format: "mp3"
          }),
        });
        
        if (!response.ok) {
          throw new Error("Failed to generate speech");
        }
        
        const result = await response.json();
        
        // Create audio element from base64
        const audioDataUrl = `data:${result.mimeType};base64,${result.audio}`;
        const audio = new Audio(audioDataUrl);
        audio.volume = (data.voiceVolume ?? 70) / 100;
        
        // Store reference for cleanup
        aiTtsAudioRef.current = audio;
        
        // Start background music if enabled
        if (backgroundMusicPlayUrl) {
          const bgAudio = new Audio(backgroundMusicPlayUrl);
          bgAudio.volume = (data.musicVolume ?? 30) / 100;
          bgAudio.loop = true;
          bgAudio.play().catch(err => {
            console.error("Error playing background music:", err);
          });
          backgroundMusicAudioRef.current = bgAudio;
        }
        
        // Play the AI voice
        audio.onended = () => {
          // Stop background music when voice ends
          setTimeout(() => {
            handleStopPreview();
          }, 500);
        };
        
        await audio.play();
        
        toast({
          title: "Playing",
          description: `Playing meditation with ${voiceType} AI voice`,
        });
        
      } catch (error) {
        console.error("TTS error:", error);
        toast({
          title: "Voice Generation Failed",
          description: "Could not generate AI voice. Please try again.",
          variant: "destructive",
        });
        setIsPreviewingFull(false);
      } finally {
        setIsGeneratingTts(false);
      }
    }
  }, [customMeditationForm, songs, mixerControls, recorderState.audioBlob, handleStopPreview, backgroundMusicEnabled, backgroundMusicSource, toast]);

  const handleTestMeditation = useCallback(async () => {
    handleHearHowItSounds();
  }, [handleHearHowItSounds]);

  // Watch volumes for preview updates
  const watchedMusicVolume = customMeditationForm.watch("musicVolume");
  const watchedVoiceVolume = customMeditationForm.watch("voiceVolume");

  // Update volumes in real-time during preview
  useEffect(() => {
    if (isPreviewingFull) {
      // Update AI TTS audio volume
      if (aiTtsAudioRef.current) {
        aiTtsAudioRef.current.volume = (watchedVoiceVolume ?? 70) / 100;
      }
      // Update background music volume
      if (backgroundMusicAudioRef.current) {
        backgroundMusicAudioRef.current.volume = (watchedMusicVolume ?? 30) / 100;
      }
      // Update mixer volumes (for recorded voice)
      mixerControls.setMusicVolume(watchedMusicVolume ?? 30);
      mixerControls.setVoiceVolume(watchedVoiceVolume ?? 70);
    }
  }, [
    isPreviewingFull,
    watchedMusicVolume,
    watchedVoiceVolume,
    mixerControls,
  ]);

  // Stop preview when mixer finishes (for recorded voice)
  useEffect(() => {
    if (isPreviewingFull && !mixerState.isPlaying && voiceType === "recorded") {
      // Mixer finished playing recorded voice
      const timeout = setTimeout(() => {
        handleStopPreview();
      }, 500);
      return () => clearTimeout(timeout);
    }
    return undefined;
  }, [mixerState.isPlaying, isPreviewingFull, voiceType, handleStopPreview]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      handleStopPreview();
    };
  }, [handleStopPreview]);

  useEffect(() => {
    if (recorderState.error) {
    }
  }, [recorderState.error]);

  useEffect(() => {
    if (mixerState.error) {
    }
  }, [mixerState.error]);

  if (playlistsLoading || songsLoading || customMeditationsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-muted-foreground">Loading meditation settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-card border-b border-border px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setLocation("/meditation")}
            data-testid="button-back"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              Meditation Settings
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage your meditation playlists and create custom meditations
            </p>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full max-w-full">
          <TabsList className="grid w-full grid-cols-2" data-testid="tabs-meditation-settings">
            <TabsTrigger value="playlist" data-testid="tab-playlist">
              Meditation Playlist
            </TabsTrigger>
            <TabsTrigger value="custom" data-testid="tab-custom">
              Create Own Meditation
            </TabsTrigger>
          </TabsList>

          <TabsContent value="playlist" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Your Meditation Playlist</CardTitle>
                    <CardDescription>
                      {createPlaylistMutation.isPending 
                        ? "Creating your playlist..." 
                        : "Manage media in your meditation playlist"}
                    </CardDescription>
                  </div>
                  <Button
                    type="button"
                    onClick={() => {
                      if (!userPlaylist && !createPlaylistMutation.isPending) {
                        // Create playlist first if none exists
                        createPlaylistMutation.mutate({ name: "My Meditation Playlist" });
                      } else if (userPlaylist) {
                        setShowSongForm(!showSongForm);
                      }
                    }}
                    disabled={createPlaylistMutation.isPending}
                    data-testid="button-add-song"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {createPlaylistMutation.isPending ? "Creating Playlist..." : "Add Media"}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {showSongForm && (
                  <Card className="mb-6">
                    <CardHeader>
                      <CardTitle className="text-lg">Add New Media</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Form {...songForm}>
                        <form onSubmit={handleAddSong} className="space-y-4">
                          <FormField
                            control={songForm.control}
                            name="title"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Title</FormLabel>
                                <FormControl>
                                  <Input
                                    {...field}
                                    placeholder="Enter media title"
                                    data-testid="input-song-title"
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={songForm.control}
                            name="artist"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Artist</FormLabel>
                                <FormControl>
                                  <Input
                                    {...field}
                                    value={field.value || ""}
                                    placeholder="Enter artist name"
                                    data-testid="input-song-artist"
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          {/* Content Type Toggle */}
                          <div className="space-y-3">
                            <Label>Content Type</Label>
                            <RadioGroup
                              value={songContentType}
                              onValueChange={(value: "meditation" | "media") => setSongContentType(value)}
                              className="flex gap-4"
                            >
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="meditation" id="content-meditation" />
                                <Label htmlFor="content-meditation" className="cursor-pointer">
                                  Meditation
                                </Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="media" id="content-media" />
                                <Label htmlFor="content-media" className="cursor-pointer">
                                  Media
                                </Label>
                              </div>
                            </RadioGroup>
                          </div>
                          {/* Source Type Selector */}
                          <div className="space-y-3">
                            <Label>Audio Source</Label>
                            <RadioGroup
                              value={songSourceType}
                              onValueChange={(value: "url" | "media" | "youtube" | "drive") => {
                                setSongSourceType(value);
                                setSelectedMediaFile(null);
                                setSelectedDriveFile(null);
                                songForm.setValue("url", "");
                              }}
                              className="flex flex-wrap gap-4"
                            >
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="url" id="source-url" />
                                <Label htmlFor="source-url" className="flex items-center gap-2 cursor-pointer">
                                  <Link2 className="w-4 h-4" />
                                  External URL
                                </Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="media" id="source-media" />
                                <Label htmlFor="source-media" className="flex items-center gap-2 cursor-pointer">
                                  <FolderOpen className="w-4 h-4" />
                                  Media Library
                                </Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="drive" id="source-drive" />
                                <Label htmlFor="source-drive" className="flex items-center gap-2 cursor-pointer">
                                  <Cloud className="w-4 h-4 text-blue-500" />
                                  Google Drive
                                </Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="youtube" id="source-youtube" />
                                <Label htmlFor="source-youtube" className="flex items-center gap-2 cursor-pointer">
                                  <Youtube className="w-4 h-4 text-red-500" />
                                  YouTube Video
                                </Label>
                              </div>
                            </RadioGroup>
                          </div>

                          {songSourceType === "youtube" ? (
                            <div className="space-y-4">
                              <FormField
                                control={songForm.control}
                                name="url"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>YouTube URL</FormLabel>
                                    <FormControl>
                                      <Input
                                        {...field}
                                        placeholder="https://www.youtube.com/watch?v=..."
                                        data-testid="input-song-youtube-url"
                                      />
                                    </FormControl>
                                    <p className="text-xs text-muted-foreground mt-1">
                                      Enter a YouTube video URL (e.g., https://www.youtube.com/watch?v=VIDEO_ID)
                                    </p>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <div className="relative">
                                <div className="absolute inset-0 flex items-center">
                                  <span className="w-full border-t" />
                                </div>
                                <div className="relative flex justify-center text-xs uppercase">
                                  <span className="bg-background px-2 text-muted-foreground">Or paste embed code</span>
                                </div>
                              </div>
                              <div>
                                <Label className="text-sm font-medium">YouTube Embed Code</Label>
                                <textarea
                                  className="mt-1.5 w-full min-h-[80px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                  placeholder='<iframe width="560" height="315" src="https://www.youtube.com/embed/VIDEO_ID" ...></iframe>'
                                  data-testid="input-song-youtube-embed"
                                  onChange={(e) => {
                                    const embedCode = e.target.value;
                                    // Extract URL from embed code
                                    const srcMatch = embedCode.match(/src=["']([^"']+)["']/);
                                    if (srcMatch && srcMatch[1]) {
                                      const embedUrl = srcMatch[1];
                                      // Convert embed URL to regular YouTube URL
                                      const videoIdMatch = embedUrl.match(/youtube\.com\/embed\/([^?&"]+)/);
                                      if (videoIdMatch && videoIdMatch[1]) {
                                        const videoId = videoIdMatch[1];
                                        songForm.setValue("url", `https://www.youtube.com/watch?v=${videoId}`);
                                      }
                                    }
                                  }}
                                />
                                <p className="text-xs text-muted-foreground mt-1">
                                  Paste the embed code from YouTube's share menu. The URL will be extracted automatically.
                                </p>
                              </div>
                            </div>
                          ) : songSourceType === "url" ? (
                            <FormField
                              control={songForm.control}
                              name="url"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Audio URL</FormLabel>
                                  <FormControl>
                                    <Input
                                      {...field}
                                      placeholder="https://example.com/audio.mp3"
                                      data-testid="input-song-url"
                                    />
                                  </FormControl>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Enter a direct URL to an MP3 or audio file
                                  </p>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          ) : songSourceType === "drive" ? (
                            <div className="space-y-3">
                              <Label>Select from Google Drive</Label>
                              {audioDriveFiles.length === 0 ? (
                                <div className="p-4 border rounded-lg bg-muted/50 text-center">
                                  <Cloud className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                                  <p className="text-sm text-muted-foreground mb-3">
                                    No audio files found in your Google Drive
                                  </p>
                                  <Link href="/files">
                                    <Button variant="outline" size="sm" data-testid="button-goto-drive">
                                      <Cloud className="w-4 h-4 mr-2" />
                                      Go to Files/Drive to Upload
                                    </Button>
                                  </Link>
                                </div>
                              ) : (
                                <>
                                  <Select
                                    value={selectedDriveFile?.id || ""}
                                    onValueChange={(value) => {
                                      const file = audioDriveFiles.find(f => f.id === value);
                                      if (file) {
                                        setSelectedDriveFile(file);
                                        // Use the streaming endpoint URL
                                        const streamUrl = `/api/google-drive/stream/${file.driveFileId}`;
                                        songForm.setValue("url", streamUrl);
                                        if (!songForm.getValues("title")) {
                                          songForm.setValue("title", file.name.replace(/\.[^.]+$/, ''));
                                        }
                                      }
                                    }}
                                  >
                                    <SelectTrigger data-testid="select-drive-file">
                                      <SelectValue placeholder="Choose an audio file from Drive..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {audioDriveFiles.map((file) => (
                                        <SelectItem key={file.id} value={file.id}>
                                          <div className="flex items-center gap-2">
                                            <Cloud className="w-4 h-4 text-blue-500" />
                                            <span>{file.name}</span>
                                          </div>
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  {selectedDriveFile && (
                                    <div className="p-3 border rounded-lg bg-blue-500/10 border-blue-500/30">
                                      <div className="flex items-center gap-2 text-sm">
                                        <Cloud className="w-4 h-4 text-blue-600" />
                                        <span className="font-medium">{selectedDriveFile.name}</span>
                                      </div>
                                    </div>
                                  )}
                                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <span>or</span>
                                    <Link href="/files">
                                      <Button variant="link" size="sm" className="h-auto p-0" data-testid="link-upload-drive">
                                        Upload new audio to Google Drive
                                      </Button>
                                    </Link>
                                  </div>
                                </>
                              )}
                              {/* Show validation error for URL */}
                              {songForm.formState.errors.url && (
                                <p className="text-sm text-destructive">{songForm.formState.errors.url.message}</p>
                              )}
                              {/* Hidden field to store the URL */}
                              <input type="hidden" {...songForm.register("url")} />
                            </div>
                          ) : (
                            <div className="space-y-3">
                              <Label>Select from Media Library</Label>
                              {audioMediaFiles.length === 0 ? (
                                <div className="p-4 border rounded-lg bg-muted/50 text-center">
                                  <Music className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                                  <p className="text-sm text-muted-foreground mb-3">
                                    No audio files found in your Media Library
                                  </p>
                                  <Link href="/media">
                                    <Button variant="outline" size="sm" data-testid="button-goto-media">
                                      <FolderOpen className="w-4 h-4 mr-2" />
                                      Go to Media Library to Upload
                                    </Button>
                                  </Link>
                                </div>
                              ) : (
                                <>
                                  <Select
                                    value={selectedMediaFile?.id || ""}
                                    onValueChange={(value) => {
                                      const file = audioMediaFiles.find(f => f.id === value);
                                      if (file) {
                                        setSelectedMediaFile(file);
                                        songForm.setValue("url", file.fileUrl);
                                        if (!songForm.getValues("title")) {
                                          songForm.setValue("title", file.name);
                                        }
                                      }
                                    }}
                                  >
                                    <SelectTrigger data-testid="select-media-file">
                                      <SelectValue placeholder="Choose an audio file..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {audioMediaFiles.map((file) => (
                                        <SelectItem key={file.id} value={file.id}>
                                          <div className="flex items-center gap-2">
                                            <Music className="w-4 h-4" />
                                            <span>{file.name}</span>
                                            <span className="text-xs text-muted-foreground">
                                              ({file.fileType})
                                            </span>
                                          </div>
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  {selectedMediaFile && (
                                    <div className="p-3 border rounded-lg bg-green-500/10 border-green-500/30">
                                      <div className="flex items-center gap-2 text-sm">
                                        <Music className="w-4 h-4 text-green-600" />
                                        <span className="font-medium">{selectedMediaFile.name}</span>
                                      </div>
                                    </div>
                                  )}
                                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <span>or</span>
                                    <Link href="/media">
                                      <Button variant="link" size="sm" className="h-auto p-0" data-testid="link-upload-media">
                                        Upload new audio to Media Library
                                      </Button>
                                    </Link>
                                  </div>
                                </>
                              )}
                              {/* Show validation error for URL */}
                              {songForm.formState.errors.url && (
                                <p className="text-sm text-destructive">{songForm.formState.errors.url.message}</p>
                              )}
                              {/* Hidden field to store the URL */}
                              <input type="hidden" {...songForm.register("url")} />
                            </div>
                          )}
                          <FormField
                            control={songForm.control}
                            name="duration"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Duration (minutes)</FormLabel>
                                <FormControl>
                                  <NumberField
                                    min={1}
                                    placeholder="5"
                                    value={field.value ? Math.round(field.value / 60) : undefined}
                                    onValueChange={(v) => field.onChange(v === undefined ? 0 : v * 60)}
                                    name={field.name}
                                    onBlur={field.onBlur}
                                    ref={field.ref}
                                    data-testid="input-song-duration"
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <div className="flex gap-2">
                            <Button
                              type="submit"
                              disabled={createSongMutation.isPending}
                              data-testid="button-submit-song"
                            >
                              {createSongMutation.isPending ? "Adding..." : "Add Media"}
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => {
                                setShowSongForm(false);
                                setSongSourceType("url");
                                setSelectedMediaFile(null);
                                setSelectedDriveFile(null);
                              }}
                              data-testid="button-cancel-song"
                            >
                              Cancel
                            </Button>
                          </div>
                        </form>
                      </Form>
                    </CardContent>
                  </Card>
                )}

                {songs.length === 0 ? (
                  <div className="text-center py-12" data-testid="empty-state-songs">
                    <Music className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      No media in your playlist yet. Add your first item!
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="list-songs">
                    {songs.map((song) => {
                      const isYouTube = song.url.includes('youtube.com') || song.url.includes('youtu.be');
                      return (
                        <Card key={song.id} data-testid={`song-${song.id}`}>
                          <CardContent className="p-4 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              {isYouTube ? (
                                <Youtube className="w-8 h-8 text-red-500" />
                              ) : (
                                <Music className="w-8 h-8 text-primary" />
                              )}
                              <div>
                                <h4 className="font-medium" data-testid={`text-song-title-${song.id}`}>
                                  {song.title}
                                </h4>
                                <p className="text-sm text-muted-foreground" data-testid={`text-song-artist-${song.id}`}>
                                  {song.artist || "Unknown Artist"} • {formatDuration(song.duration)}
                                  {isYouTube && " • YouTube"}
                                </p>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteSongMutation.mutate(song.id)}
                              data-testid={`button-delete-song-${song.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="custom" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Create Custom Meditation</CardTitle>
                <CardDescription>
                  Design your personalized meditation experience
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Form {...customMeditationForm}>
                  <form onSubmit={handleSaveCustomMeditation} className="space-y-6">
                    <FormField
                      control={customMeditationForm.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Meditation Name</FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder="Enter meditation name"
                              data-testid="input-meditation-name"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={customMeditationForm.control}
                      name="scriptText"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Meditation Script</FormLabel>
                          <FormControl>
                            <Textarea
                              {...field}
                              placeholder="Enter your meditation script here..."
                              className="min-h-[200px]"
                              data-testid="textarea-meditation-script"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={customMeditationForm.control}
                      name="voiceType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Voice Selection</FormLabel>
                          <FormDescription className="text-xs">
                            Choose an AI voice powered by OpenAI, or record your own
                          </FormDescription>
                          <FormControl>
                            <RadioGroup
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                              className="grid grid-cols-1 sm:grid-cols-2 gap-2"
                              data-testid="radio-voice-selection"
                            >
                              <div className="flex items-center space-x-2 p-2 rounded-md border hover:bg-muted/50">
                                <RadioGroupItem value="nova" id="nova" data-testid="radio-nova-voice" />
                                <Label htmlFor="nova" className="flex-1 cursor-pointer">
                                  <span className="font-medium">Nova</span>
                                  <span className="text-xs text-muted-foreground block">Warm female voice</span>
                                </Label>
                              </div>
                              <div className="flex items-center space-x-2 p-2 rounded-md border hover:bg-muted/50">
                                <RadioGroupItem value="shimmer" id="shimmer" data-testid="radio-shimmer-voice" />
                                <Label htmlFor="shimmer" className="flex-1 cursor-pointer">
                                  <span className="font-medium">Shimmer</span>
                                  <span className="text-xs text-muted-foreground block">Soft female voice</span>
                                </Label>
                              </div>
                              <div className="flex items-center space-x-2 p-2 rounded-md border hover:bg-muted/50">
                                <RadioGroupItem value="onyx" id="onyx" data-testid="radio-onyx-voice" />
                                <Label htmlFor="onyx" className="flex-1 cursor-pointer">
                                  <span className="font-medium">Onyx</span>
                                  <span className="text-xs text-muted-foreground block">Deep male voice</span>
                                </Label>
                              </div>
                              <div className="flex items-center space-x-2 p-2 rounded-md border hover:bg-muted/50">
                                <RadioGroupItem value="echo" id="echo" data-testid="radio-echo-voice" />
                                <Label htmlFor="echo" className="flex-1 cursor-pointer">
                                  <span className="font-medium">Echo</span>
                                  <span className="text-xs text-muted-foreground block">Warm male voice</span>
                                </Label>
                              </div>
                              <div className="flex items-center space-x-2 p-2 rounded-md border hover:bg-muted/50">
                                <RadioGroupItem value="alloy" id="alloy" data-testid="radio-alloy-voice" />
                                <Label htmlFor="alloy" className="flex-1 cursor-pointer">
                                  <span className="font-medium">Alloy</span>
                                  <span className="text-xs text-muted-foreground block">Neutral voice</span>
                                </Label>
                              </div>
                              <div className="flex items-center space-x-2 p-2 rounded-md border hover:bg-muted/50">
                                <RadioGroupItem value="fable" id="fable" data-testid="radio-fable-voice" />
                                <Label htmlFor="fable" className="flex-1 cursor-pointer">
                                  <span className="font-medium">Fable</span>
                                  <span className="text-xs text-muted-foreground block">Expressive voice</span>
                                </Label>
                              </div>
                              <div className="flex items-center space-x-2 p-2 rounded-md border hover:bg-muted/50 sm:col-span-2">
                                <RadioGroupItem value="recorded" id="recorded" data-testid="radio-record-voice" />
                                <Label htmlFor="recorded" className="flex-1 cursor-pointer">
                                  <span className="font-medium">Record My Voice</span>
                                  <span className="text-xs text-muted-foreground block">Use your own recorded narration</span>
                                </Label>
                              </div>
                            </RadioGroup>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {showRecording && (
                      <Card data-testid="recording-interface">
                        <CardContent className="pt-6">
                          <div className="space-y-4">
                            <div className="flex items-center justify-center gap-4">
                              {!recorderState.isRecording && !recorderState.audioURL ? (
                                <Button
                                  type="button"
                                  onClick={recorderControls.startRecording}
                                  data-testid="button-start-recording"
                                >
                                  <Mic className="w-4 h-4 mr-2" />
                                  Start Recording
                                </Button>
                              ) : recorderState.isRecording ? (
                                <Button
                                  type="button"
                                  variant="destructive"
                                  onClick={recorderControls.stopRecording}
                                  data-testid="button-stop-recording"
                                >
                                  <Square className="w-4 h-4 mr-2" />
                                  Stop Recording
                                </Button>
                              ) : null}
                            </div>

                            {recorderState.isRecording && (
                              <div className="text-center">
                                <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/10 text-red-500 rounded-full">
                                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                                  <span className="font-mono text-sm">
                                    {formatTime(recorderState.recordingTime)}
                                  </span>
                                </div>
                                <p className="text-sm text-muted-foreground mt-2">
                                  Recording in progress...
                                </p>
                              </div>
                            )}

                            {recorderState.audioURL && (
                              <div className="space-y-3">
                                <div className="bg-muted rounded-lg p-4">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <Volume2 className="w-5 h-5 text-primary" />
                                      <span className="text-sm font-medium">
                                        Recording: {formatTime(recorderState.recordingTime)}
                                      </span>
                                    </div>
                                    <div className="flex gap-2">
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        onClick={recorderControls.playRecording}
                                        data-testid="button-play-recording"
                                      >
                                        <Play className="w-4 h-4 mr-1" />
                                        Play
                                      </Button>
                                      <Button
                                        type="button"
                                        size="sm"
                                        variant="outline"
                                        onClick={recorderControls.deleteRecording}
                                        data-testid="button-delete-recording"
                                      >
                                        <Trash className="w-4 h-4 mr-1" />
                                        Delete
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="background-music-toggle" className="font-medium">
                          Background Music
                        </Label>
                        <Switch
                          id="background-music-toggle"
                          checked={backgroundMusicEnabled}
                          onCheckedChange={setBackgroundMusicEnabled}
                          data-testid="switch-background-music"
                        />
                      </div>

                      {backgroundMusicEnabled && (
                        <div className="space-y-4 pl-4 border-l-2 border-muted">
                          <RadioGroup
                            value={backgroundMusicSource}
                            onValueChange={(value) => setBackgroundMusicSource(value as "library" | "url")}
                            className="flex gap-4"
                          >
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="library" id="music-library" />
                              <Label htmlFor="music-library">From Library</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="url" id="music-url" />
                              <Label htmlFor="music-url">External URL</Label>
                            </div>
                          </RadioGroup>

                          {backgroundMusicSource === "library" && (
                            <FormField
                              control={customMeditationForm.control}
                              name="backgroundMusicId"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Select Track</FormLabel>
                                  <Select onValueChange={field.onChange} value={field.value || undefined}>
                                    <FormControl>
                                      <SelectTrigger data-testid="select-background-music">
                                        <SelectValue placeholder="Select background music" />
                                      </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                      {songs.map((song) => (
                                        <SelectItem key={song.id} value={song.id}>
                                          {song.title}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          )}

                          {backgroundMusicSource === "url" && (
                            <FormField
                              control={customMeditationForm.control}
                              name="backgroundMusicUrl"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Music URL</FormLabel>
                                  <FormControl>
                                    <Input
                                      {...field}
                                      value={field.value || ""}
                                      placeholder="https://example.com/royalty-free-music.mp3"
                                      data-testid="input-background-music-url"
                                    />
                                  </FormControl>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Enter a direct URL to royalty-free background music (MP3, WAV, etc.)
                                  </p>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          )}

                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => window.open("https://pixabay.com/music/search/meditation/", "_blank")}
                            className="w-full"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Find Free Background Music
                          </Button>

                          <FormField
                            control={customMeditationForm.control}
                            name="musicVolume"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Music Volume: {field.value}%</FormLabel>
                                <FormControl>
                                  <Slider
                                    min={0}
                                    max={100}
                                    step={1}
                                    value={[field.value ?? 30]}
                                    onValueChange={(vals) => field.onChange(vals[0])}
                                    data-testid="slider-music-volume"
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      )}
                    </div>

                    <FormField
                      control={customMeditationForm.control}
                      name="voiceVolume"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Voice Volume: {field.value}%</FormLabel>
                          <FormControl>
                            <Slider
                              min={0}
                              max={100}
                              step={1}
                              value={[field.value ?? 70]}
                              onValueChange={(vals) => field.onChange(vals[0])}
                              data-testid="slider-voice-volume"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={customMeditationForm.control}
                      name="playbackSpeed"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            Playback Speed: {field.value ?? 5} {(field.value ?? 5) === 5 ? '(Normal)' : (field.value ?? 5) < 5 ? '(Slower)' : '(Faster)'}
                          </FormLabel>
                          <FormControl>
                            <Slider
                              min={0}
                              max={10}
                              step={0.5}
                              value={[field.value ?? 5]}
                              onValueChange={(vals) => field.onChange(vals[0])}
                              data-testid="slider-playback-speed"
                            />
                          </FormControl>
                          <p className="text-xs text-muted-foreground mt-1">
                            0 = Slowest, 5 = Normal, 10 = Fastest
                          </p>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="space-y-4">
                      {/* Hear How It Sounds Button */}
                      <Card className="border-2 border-primary/20 bg-primary/5">
                        <CardContent className="pt-6">
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="font-semibold text-lg">Preview Your Meditation</h4>
                                <p className="text-sm text-muted-foreground">
                                  {voiceType === "recorded" && !recorderState.audioBlob
                                    ? "Record your voice first to preview"
                                    : "Test your meditation with voice and background music"}
                                </p>
                              </div>
                            </div>
                            
                            <div className="flex flex-wrap gap-2">
                              {!isPreviewingFull ? (
                                <>
                                  <Button
                                    type="button"
                                    size="lg"
                                    onClick={handleHearHowItSounds}
                                    disabled={
                                      (voiceType === "recorded" && !recorderState.audioBlob) ||
                                      isGeneratingTts
                                    }
                                    className="flex-1 min-w-[200px]"
                                    data-testid="button-hear-how-it-sounds"
                                  >
                                    {isGeneratingTts ? (
                                      <>
                                        <div className="w-5 h-5 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                        Generating Voice...
                                      </>
                                    ) : (
                                      <>
                                        <Volume2 className="w-5 h-5 mr-2" />
                                        Hear How It Sounds
                                      </>
                                    )}
                                  </Button>
                                  {voiceType !== "recorded" && (
                                    <Button
                                      type="button"
                                      variant="outline"
                                      onClick={handlePreviewTTS}
                                      disabled={isGeneratingTts}
                                      data-testid="button-preview-voice-only"
                                    >
                                      <PlayCircle className="w-4 h-4 mr-2" />
                                      {isGeneratingTts ? "Generating..." : "Voice Only"}
                                    </Button>
                                  )}
                                </>
                              ) : (
                                <Button
                                  type="button"
                                  size="lg"
                                  variant="destructive"
                                  onClick={handleStopPreview}
                                  className="flex-1 min-w-[200px]"
                                  data-testid="button-stop-preview"
                                >
                                  <Square className="w-5 h-5 mr-2" />
                                  Stop Preview
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Preview Status */}
                      {isPreviewingFull && (
                        <Card>
                          <CardContent className="pt-4">
                            <div className="space-y-3">
                              <div className="flex items-center justify-center gap-2 text-primary">
                                <div className="w-3 h-3 bg-primary rounded-full animate-pulse" />
                                <span className="font-medium">
                                  {voiceType === "recorded" ? "Playing recorded meditation" : `Playing with ${voiceType} voice`}
                                </span>
                              </div>
                              
                              {voiceType === "recorded" && mixerState.isPlaying && (
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between text-sm">
                                    <span className="text-muted-foreground">
                                      {formatTime(mixerState.currentTime)} / {formatTime(mixerState.duration)}
                                    </span>
                                  </div>
                                  <div className="w-full bg-secondary rounded-full h-2">
                                    <div
                                      className="bg-primary h-2 rounded-full transition-all"
                                      style={{ 
                                        width: `${(mixerState.currentTime / mixerState.duration) * 100}%` 
                                      }}
                                    />
                                  </div>
                                </div>
                              )}
                              
                              <p className="text-xs text-muted-foreground text-center">
                                💡 Adjust the volume sliders above to fine-tune your meditation while it plays
                              </p>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Old mixer preview (keeping for recorded voice playback progress) */}
                      {!isPreviewingFull && mixerState.isPlaying && (
                        <Card>
                          <CardContent className="pt-4">
                            <div className="space-y-2">
                              <div className="flex items-center justify-between text-sm">
                                <span className="text-muted-foreground">
                                  {formatTime(mixerState.currentTime)} / {formatTime(mixerState.duration)}
                                </span>
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={mixerControls.pause}
                                    data-testid="button-pause-mixer"
                                  >
                                    <Pause className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={mixerControls.stop}
                                    data-testid="button-stop-mixer"
                                  >
                                    <Square className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                              <div className="w-full bg-secondary rounded-full h-2">
                                <div
                                  className="bg-primary h-2 rounded-full transition-all"
                                  style={{ width: `${(mixerState.currentTime / mixerState.duration) * 100}%` }}
                                />
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      <Button
                        type="submit"
                        disabled={createCustomMeditationMutation.isPending}
                        className="w-full"
                        data-testid="button-save-meditation"
                      >
                        {createCustomMeditationMutation.isPending ? "Saving..." : "Save Meditation"}
                      </Button>
                    </div>
                  </form>
                </Form>
              </CardContent>
            </Card>

            {customMeditations.length > 0 && (
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle>Saved Custom Meditations</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3" data-testid="list-custom-meditations">
                    {customMeditations.map((meditation) => (
                      <Card key={meditation.id} data-testid={`meditation-${meditation.id}`}>
                        <CardContent className="p-4 flex items-center justify-between">
                          <div>
                            <h4 className="font-medium" data-testid={`text-meditation-name-${meditation.id}`}>
                              {meditation.name}
                            </h4>
                            <p className="text-sm text-muted-foreground">
                              {meditation.voiceType} voice • Music: {meditation.musicVolume}% • Voice: {meditation.voiceVolume}%
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              data-testid={`button-edit-meditation-${meditation.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteCustomMeditationMutation.mutate(meditation.id)}
                              data-testid={`button-delete-meditation-${meditation.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
